import fs from "node:fs";
import path from "node:path";
import {
  FAIL_CLASS_DECL,
  FAILZ_CLASS_DECL,
  FIN_DECL,
  FIN_DEFERRED_DECL,
  FINZ_DECL,
  generateIIFE,
  MK_VALIDATOR_DECL,
  ZOD_CONFIG_IMPORT,
  ZOD_MSG_DECLARATION,
} from "../core/iife.js";
import type { CompiledSchemaInfo } from "../core/pipeline.js";

/**
 * Generate the content of a .compiled.ts file from multiple schemas.
 */
export interface EmitterOptions {
  /**
   * When true (default), installs the compiled methods on the original schema
   * object for Zod-compatible output.
   * When false, produces a minimal plain object (smaller bundle).
   */
  zodCompat?: boolean | undefined;
  /**
   * File-level shared validator and constant declarations. Emitted once at
   * module scope so each compiled export can reference them.
   */
  sharedCode?: string | undefined;
}

export function generateCompiledFileContent(
  schemas: CompiledSchemaInfo[],
  sourceRelPath: string,
  options?: EmitterOptions,
): string {
  let importPath = sourceRelPath.replace(/\.[cm]?[jt]sx?$/, "");
  if (!importPath.startsWith(".")) {
    importPath = `./${importPath}`;
  }

  const zodCompat = options?.zodCompat !== false;

  // Compact-mode schemas delegate cold errors to zod via __zcFinZ (its own
  // __ZcFailZ class), emitted only when some export uses it.
  const usesFinZ = schemas.some((s) => s.codegenResult.usedHelpers.has("__zcFinZ"));

  // A schema needs the source import when:
  //   - zodCompat: true (__zcMkv installs compiled methods on the schema)
  //   - has refEntries (fallback schemas referenced via __rf[])
  //   - delegates its cold path to the retained schema (reads the __zs binding)
  //
  // The last clause must stay in lockstep with generateIIFE's own test for
  // emitting `var __zs=<expr>;`: hand it an empty expression for a schema it
  // decides to bind and the file is a syntax error (`var __zs=;`).
  const needsSource = (s: CompiledSchemaInfo): boolean =>
    zodCompat || s.refEntries.length > 0 || s.codegenResult.usesRetainedSchema === true;
  const schemasNeedingImport = schemas.filter(needsSource);

  const exports = schemas.map((schema) => {
    const needsImport = needsSource(schema);
    // `compile()` returns the schema augmented with a non-enumerable `schema`
    // self-reference; auto-discovery (the default) instead finds plain exported
    // Zod schemas, which have no such property. Both shapes must resolve, so
    // fall through to the export itself — reading `.schema` alone yields
    // undefined there, which silently cost __zcMkv the real schema to augment
    // and hard-crashed every `__rf[]` base at module load.
    const src = `__src_${schema.exportName}`;
    const schemaExpr = needsImport ? `((${src} as any).schema ?? ${src})` : "";
    return `export const ${schema.exportName} = ${generateIIFE(schemaExpr, schema, { zodCompat })};`;
  });

  const importLine =
    schemasNeedingImport.length > 0
      ? [
          `import { ${schemasNeedingImport.map((s) => `${s.exportName} as __src_${s.exportName}`).join(", ")} } from "${importPath}";`,
          "",
        ]
      : [];

  return [
    "// AUTO-GENERATED by zod-compiler — DO NOT EDIT",
    `// Source: ${sourceRelPath}`,
    "",
    ZOD_CONFIG_IMPORT,
    ZOD_MSG_DECLARATION,
    FAIL_CLASS_DECL,
    MK_VALIDATOR_DECL,
    FIN_DECL,
    FIN_DEFERRED_DECL,
    ...(usesFinZ ? [FAILZ_CLASS_DECL, FINZ_DECL] : []),
    ...(options?.sharedCode ? ["", options.sharedCode] : []),
    "",
    ...importLine,
    ...exports,
    "",
  ].join("\n");
}

/**
 * Resolve the output file path for a given input file.
 */
export function resolveOutputPath(inputPath: string, outputFlag: string | undefined): string {
  if (outputFlag) {
    // If output flag ends with / or is a directory, put the compiled file inside it
    if (outputFlag.endsWith("/") || outputFlag.endsWith(path.sep)) {
      const baseName = path.basename(inputPath, path.extname(inputPath));
      return path.join(outputFlag, `${baseName}.compiled.ts`);
    }
    return outputFlag;
  }

  // Default: replace .ts with .compiled.ts
  const dir = path.dirname(inputPath);
  const baseName = path.basename(inputPath, path.extname(inputPath));
  return path.join(dir, `${baseName}.compiled.ts`);
}

/**
 * Write the compiled file content to disk.
 */
export async function writeCompiledFile(outputPath: string, content: string): Promise<void> {
  const dir = path.dirname(outputPath);
  await fs.promises.mkdir(dir, { recursive: true });
  await fs.promises.writeFile(outputPath, content, "utf-8");
}
